import * as React from "react";
import { useEffect, useRef } from "react";
import {
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
} from "motion/react";

type FontValue =
  | string
  | {
      fontFamily?: string;
      fontWeight?: number | string;
      fontSize?: number | string;
      fontStyle?: string;
      letterSpacing?: string | number;
      lineHeight?: string | number;
      textAlign?: "left" | "center" | "right";
    };

type FlashlightTextProps = {
  text: string;
  font?: FontValue;
  brightColor?: string;
  dimColor?: string;
  maskSize?: number;
  intensity?: number;
  transition?: Parameters<typeof animate>[2];
  style?: React.CSSProperties;
  className?: string;
};

function resolveFont(font: FontValue | undefined): React.CSSProperties {
  if (!font) return {};
  if (typeof font === "string") return { fontFamily: font };
  return font as React.CSSProperties;
}

const DEFAULTS = {
  brightColor: "#FFFFFF",
  dimColor: "#3A3A3A",
  maskSize: 260,
  intensity: 12,
  transition: { type: "tween", duration: 0.35, ease: "easeInOut" } as Parameters<typeof animate>[2],
};

export default function FlashlightText({
  text,
  font,
  brightColor = DEFAULTS.brightColor,
  dimColor = DEFAULTS.dimColor,
  maskSize = DEFAULTS.maskSize,
  intensity = DEFAULTS.intensity,
  transition = DEFAULTS.transition,
  style,
  className,
}: FlashlightTextProps) {
  const prefersReducedMotion = useReducedMotion();
  const interactive = !prefersReducedMotion;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const maskX = useMotionValue(0);
  const maskY = useMotionValue(0);
  const maskSizeMV = useMotionValue(0);

  const core = Math.max(10, Math.min(100, intensity));
  const maskImage = useMotionTemplate`radial-gradient(circle ${maskSizeMV}px at ${maskX}px ${maskY}px, black, black ${core}%, transparent 100%)`;

  useEffect(() => {
    if (!interactive) return;
    const el = containerRef.current;
    if (!el) return;

    const onMove = (e: PointerEvent) => {
      const rect = (contentRef.current ?? el).getBoundingClientRect();
      maskX.set(e.clientX - rect.left);
      maskY.set(e.clientY - rect.top);
    };
    const onEnter = () => {
      animate(maskSizeMV, maskSize, transition);
    };
    const onLeave = () => {
      animate(maskSizeMV, 0, transition);
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerleave", onLeave);

    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [interactive, maskSize, transition, maskX, maskY, maskSizeMV]);

  useEffect(() => {
    if (interactive) return;
    const el = contentRef.current;
    const w = el?.clientWidth ?? 720;
    const h = el?.clientHeight ?? 240;
    maskX.set(w / 2);
    maskY.set(h / 2);
    maskSizeMV.set(maskSize);
  }, [interactive, maskSize, maskX, maskY, maskSizeMV]);

  const fontStyles = resolveFont(font);

  const textTypography: React.CSSProperties = {
    margin: 0,
    boxSizing: "border-box",
    width: "100%",
    fontFamily:
      (fontStyles.fontFamily as string) ||
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    ...fontStyles,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    userSelect: "none",
  };

  const rootStyle: React.CSSProperties = {
    ...style,
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    cursor: interactive ? "none" : undefined,
  };

  return (
    <div ref={containerRef} style={rootStyle} className={className}>
      <div ref={contentRef} style={{ position: "relative", width: "100%" }}>
        <div
          aria-label={text}
          style={{
            ...textTypography,
            position: "relative",
            color: dimColor,
          }}
        >
          {text}
        </div>

        <motion.div
          aria-hidden
          style={{
            ...textTypography,
            position: "absolute",
            top: 0,
            left: 0,
            color: brightColor,
            pointerEvents: "none",
            WebkitMaskImage: maskImage,
            maskImage: maskImage,
            WebkitMaskSize: "100%",
            maskSize: "100%",
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
          }}
        >
          {text}
        </motion.div>
      </div>
    </div>
  );
}